import React from "react";
import GoogleMapReact from "google-map-react";

import * as simpleMapStyle from "../../components/MapView/simpleStyle.json";
import { MapMarker } from "./MapMarker";
import { ScheduleItem } from "../../../../models";
import { useAppSelector } from "../../../../app/hooks";

export interface MapProps {
  scheduleItems: ScheduleItem[];
  deliveries: ScheduleItem[];
  pickups: ScheduleItem[];
  dumpYards: ScheduleItem[];
}

export const Map: React.FC<MapProps> = ({
  deliveries,
  pickups,
  dumpYards,
  scheduleItems,
}) => {
  const allDeliveries = useAppSelector((state) => state.deliveries.deliveries);
  const allPickups = useAppSelector((state) => state.pickups.pickups);
  const allDumpYards = useAppSelector((state) => state.dumpYards.dumpYards);
  const templates = useAppSelector((state) => state.dumpYards.templates);

  const defaultProps = {
    center: {
      lat: 40.40016,
      lng: -80.04932,
    },
    zoom: 11,
  };

  const renderDeliveries = (item: ScheduleItem): React.ReactNode => {
    const delivery = allDeliveries.find((d) => d.id === item.deliveryId);
    if (!delivery) return null;

    return (
      <MapMarker
        key={item.id}
        orderNumber={item.orderNumber + ""}
        lat={delivery.latitude}
        lng={delivery.longitude}
        className="delivery"
      />
    );
  };

  const renderPickups = (item: ScheduleItem): React.ReactNode => {
    const pickup = allPickups.find((p) => p.id === item.pickupId);
    if (!pickup) return null;

    return (
      <MapMarker
        key={item.id}
        orderNumber={item.orderNumber + ""}
        lat={pickup.latitude}
        lng={pickup.longitude}
        className="pick_up"
      />
    );
  };

  const renderDumpYards = (items: ScheduleItem[]): React.ReactNode[] => {
    const dumpyards: { [key: string]: string[] } = {};
    const storages: { [key: string]: string[] } = {};
    const quickstops: { [key: string]: string[] } = {};

    items.forEach((item) => {
      const dumpYard = allDumpYards.find((d) => d.id === item.dumpYardId);
      if (!dumpYard) return;

      const template = templates.find((t) => t.id === dumpYard.templateId);
      if (!template) return;

      const orderNumber = item.orderNumber + "";

      if (template.isDumpYard) {
        if (!dumpyards[template.id]) dumpyards[template.id] = [];
        dumpyards[template.id].push(orderNumber);
      } else if (template.isStorage) {
        if (!storages[template.id]) storages[template.id] = [];
        storages[template.id].push(orderNumber);
      } else if (template.isQuickStop) {
        if (!quickstops[template.id]) quickstops[template.id] = [];
        quickstops[template.id].push(orderNumber);
      }
    });

    const renderMarkers = (
      items: { [key: string]: string[] },
      className: string
    ): React.ReactNode[] => {
      return Object.keys(items).map((templateId) => {
        const template = templates.find((t) => t.id === templateId);
        if (!template) return null;

        return (
          <MapMarker
            key={templateId}
            orderNumber={items[templateId].join(", ")}
            lat={template.latitude}
            lng={template.longitude}
            className={className}
          />
        );
      });
    };

    return [
      ...renderMarkers(dumpyards, "dump_yard"),
      ...renderMarkers(storages, "storage"),
      ...renderMarkers(quickstops, "quickstop"),
    ];
  };

  const handleApiLoaded = (map: any, maps: any) => {
    const pathCoords = scheduleItems
      .map((item) => {
        const delivery = allDeliveries.find((d) => d.id === item.deliveryId);
        const pickup = allPickups.find((p) => p.id === item.pickupId);
        const dumpYard = allDumpYards.find((d) => d.id === item.dumpYardId);
        const template = dumpYard
          ? templates.find((t) => t.id === dumpYard.templateId)
          : null;

        if (delivery) {
          return { lat: delivery.latitude, lng: delivery.longitude };
        } else if (pickup) {
          return { lat: pickup.latitude, lng: pickup.longitude };
        } else if (template) {
          return { lat: template.latitude, lng: template.longitude };
        } else {
          return null;
        }
      })
      .filter((coord) => coord !== null) as { lat: number; lng: number }[];

    // Create the polyline
    const route = new maps.Polyline({
      path: pathCoords,
      geodesic: true,
      strokeColor: "#FF0000",
      strokeOpacity: 1.0,
      strokeWeight: 2,
      icons: [
        {
          icon: {
            path: maps.SymbolPath.FORWARD_CLOSED_ARROW,
            scale: 1.5,
            strokeWeight: 2,
            fillColor: "#FF0000",
            fillOpacity: 1,
          },
          offset: "50%",
          repeat: "100px", // Repeat arrow every 100 pixels
        },
      ],
    });

    // Add the polyline to the map
    route.setMap(map);
  };

  return (
    <GoogleMapReact
      bootstrapURLKeys={{
        key: "AIzaSyCkRYkFYLEOcaEbwmSYJXaQ2jlIttKkl8M",
        version: "3.58.8",
      }}
      defaultCenter={defaultProps.center}
      defaultZoom={defaultProps.zoom}
      options={{
        styles: simpleMapStyle,
      }}
      yesIWantToUseGoogleMapApiInternals
      onGoogleApiLoaded={({ map, maps }) => handleApiLoaded(map, maps)}
    >
      {deliveries.map(renderDeliveries)}
      {pickups.map(renderPickups)}
      {renderDumpYards(dumpYards)}
    </GoogleMapReact>
  );
};
